import { and, eq, gte, lte } from "drizzle-orm";
import { meal_plan_entry, type PowerSyncDrizzleDB } from "@mealplan/powersync";
import { nowISO } from "../utils/date-helpers";
import { generateId } from "@fleet/utils";
import { addDays, type MealType, toDateKey, weekDateKeys } from "./queries";

export async function addMealPlanEntryMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  input: {
    planDate: string;
    mealType: MealType;
    recipeId: string;
    servings?: number | null;
    note?: string | null;
  },
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(meal_plan_entry).values({
    id,
    user_id: userId,
    plan_date: input.planDate,
    meal_type: input.mealType,
    recipe_id: input.recipeId,
    servings: input.servings ?? null,
    note: input.note?.trim() || null,
    created_at: now,
    updated_at: now,
  });
  return id;
}

// ⚠️ No callers in apps/ (dead domain, no live routes use it): intentionally
// does NOT harden it with `patchRowVerified` (see `../powersync/verified-write.ts`)
// — there is nothing to protect. If a caller appears in the future, wrap it:
// `patchRowVerified(db, meal_plan_entry, meal_plan_entry.id, entryId, updates, options)`.
export async function updateMealPlanEntryMutation(
  db: PowerSyncDrizzleDB,
  entryId: string,
  updates: Partial<{ servings: number | null; note: string | null }>,
): Promise<void> {
  await db
    .update(meal_plan_entry)
    .set({ ...updates, updated_at: nowISO() })
    .where(eq(meal_plan_entry.id, entryId));
}

export async function removeMealPlanEntryMutation(
  db: PowerSyncDrizzleDB,
  entryId: string,
): Promise<void> {
  await db.delete(meal_plan_entry).where(eq(meal_plan_entry.id, entryId));
}

const DAYS_PER_WEEK = 7;

/**
 * Duplicates all entries from the week starting on `fromWeekStart`
 * (YYYY-MM-DD, Monday) to the week starting on `toWeekStart`.
 * Existing destination entries are preserved (the plan allows several
 * recipes per day+meal).
 */
export async function duplicateWeekMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  fromWeekStart: string,
  toWeekStart: string,
): Promise<number> {
  const [fy = 0, fm = 1, fd = 1] = fromWeekStart.split("-").map(Number);
  const fromStartDate = new Date(fy, fm - 1, fd);
  const fromKeys = weekDateKeys(fromStartDate);
  const fromEnd = fromKeys.at(-1) ?? fromWeekStart;

  const [ty = 0, tm = 1, td = 1] = toWeekStart.split("-").map(Number);
  const toStartDate = new Date(ty, tm - 1, td);

  const entries = await db
    .select()
    .from(meal_plan_entry)
    .where(
      and(
        eq(meal_plan_entry.user_id, userId),
        gte(meal_plan_entry.plan_date, fromWeekStart),
        lte(meal_plan_entry.plan_date, fromEnd),
      ),
    );

  if (entries.length === 0) {
    return 0;
  }

  const now = nowISO();
  const offsetByDate = new Map<string, string>();
  for (let i = 0; i < DAYS_PER_WEEK; i += 1) {
    const fromKey = fromKeys[i];
    if (fromKey !== undefined) {
      offsetByDate.set(fromKey, toDateKey(addDays(toStartDate, i)));
    }
  }

  await db.transaction(async (trx) => {
    for (const entry of entries) {
      const targetDate = entry.plan_date ? offsetByDate.get(entry.plan_date) : undefined;
      if (!targetDate) {
        continue;
      }
      await trx.insert(meal_plan_entry).values({
        id: generateId(),
        user_id: userId,
        plan_date: targetDate,
        meal_type: entry.meal_type,
        recipe_id: entry.recipe_id,
        servings: entry.servings,
        note: entry.note,
        created_at: now,
        updated_at: now,
      });
    }
  });

  return entries.length;
}
